import { Link } from 'react-router-dom';
import {
  ArrowRight, MapPin,
  Users, Star, Calendar, Mail,
  FileText, BarChart3, Zap
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

export interface LocationData {
  city: string;
  state: string;
  stateAbbr: string;
  slug: string;
  heroTitle: string;
  heroSubtitle: string;
  metaDescription: string;
  industries: { name: string; icon: string; description: string }[];
  stats: { label: string; value: string }[];
  neighborhoods: string[];
  testimonial: { quote: string; name: string; role: string; company: string };
  localInsight: string;
  jobMarketDescription: string;
}

export default function LocationPage({ data }: { data: LocationData }) {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-8">
              <MapPin className="w-4 h-4" />
              {data.city}, {data.stateAbbr}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              {data.heroTitle}
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {data.heroSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
              >
                Start Hiring in {data.city}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
              >
                Talk to Sales
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {data.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Job Market */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
                  Local Market
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  The {data.city} hiring landscape
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {data.jobMarketDescription}
                </p>
                <p className="text-gray-600 leading-relaxed">
                  {data.localInsight}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Zap, label: 'Instant auto-emails', desc: 'Respond to every applicant automatically' },
                  { icon: Calendar, label: 'Calendly scheduling', desc: 'Applicants self-schedule interviews' },
                  { icon: FileText, label: 'Custom forms', desc: 'Build branded application forms' },
                  { icon: BarChart3, label: 'Hiring analytics', desc: 'Track your pipeline performance' },
                ].map((feature, i) => (
                  <div key={i} className="card p-5">
                    <feature.icon className="w-8 h-8 text-primary-600 mb-3" />
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{feature.label}</h3>
                    <p className="text-xs text-gray-500">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Industries */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Industries
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Top hiring industries in {data.city}
            </h2>
            <p className="text-lg text-gray-600">
              iHireFast works for any industry. Here are the top sectors hiring in {data.city}, {data.stateAbbr}.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {data.industries.map((industry, i) => (
              <div key={i} className="card p-6 group hover:shadow-lg transition-all duration-300">
                <div className="text-3xl mb-4">{industry.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{industry.name}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works locally */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Start hiring in {data.city} in minutes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Create Your Account', desc: 'Sign up with Google in 30 seconds. Set up your company profile.', icon: Users },
              { step: '02', title: 'Build a Form', desc: 'Create a branded application form for your open position.', icon: FileText },
              { step: '03', title: 'Get Applicants', desc: 'Share your form link or embed it on your website. Applicants get auto-emailed instantly.', icon: Mail },
              { step: '04', title: 'Schedule & Hire', desc: 'Applicants self-schedule via Calendly. Show up, interview, and hire.', icon: Calendar },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-primary-600" />
                </div>
                <div className="text-xs font-bold text-primary-600 mb-2">STEP {step.step}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <blockquote className="text-xl sm:text-2xl font-medium text-gray-900 leading-relaxed mb-8">
              "{data.testimonial.quote}"
            </blockquote>
            <div>
              <div className="font-semibold text-gray-900">{data.testimonial.name}</div>
              <div className="text-sm text-gray-500">{data.testimonial.role} at {data.testimonial.company}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods / Areas */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Serving all of {data.city} and surrounding areas
            </h2>
            <p className="text-gray-600">
              Whether you're hiring in downtown {data.city} or the surrounding suburbs, iHireFast helps you find great candidates fast.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {data.neighborhoods.map((area, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to hire faster in {data.city}?
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Join hundreds of {data.city} businesses using iHireFast to streamline recruiting. Free to start, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Locations */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="container-custom">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">We also serve</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { name: 'Miami', slug: 'miami' },
                { name: 'Fort Lauderdale', slug: 'fort-lauderdale' },
                { name: 'West Palm Beach', slug: 'west-palm-beach' },
              ]
                .filter((loc) => loc.slug !== data.slug)
                .map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/locations/${loc.slug}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 transition-all"
                  >
                    <MapPin className="w-4 h-4" />
                    {loc.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
